import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import axios, { Canceler } from 'axios';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'redux';
import {
  IHotel,
  ILocation,
  IRoom,
  IHotelsResponse,
  ICounters,
  ISupplier,
  ISessionKey,
  IBounds,
  IHotelsRecentSearches,
} from 'src/common-types/index';
import {
  DEFAULT_PAGE_NUMBER,
  DEFAULT_MAP_LIMIT,
  Urls,
  SESSION_EXPIRED_STATUS,
  SESSION_EXPIRED_MESSAGE,
  SESSION_KEY_LABEL,
} from 'src/constants/index';
import { getHeaders, getSearchBody, axiosInstance, UrlUtils, getTimeout } from 'src/utils/index';
import { AppThunk, RootState } from 'src/store';

const oneItem = 1;
const zeroItem = 0;
const reRenderNum = 0.000000001;

export interface IHotelsState {
  hotels: IHotel[];
  mapHotels: IHotel[];
  isMapView: boolean;
  loading: boolean;
  mapLoading: boolean;
  loadingMore: boolean;
  loadingFilters: boolean;
  error: string;
  sessionKey?: ISessionKey;
  isSearch: boolean;
  pageNumber: number;
  searchedLocation: string;
  lastSearchedLocation: ILocation;
  counters: ICounters;
  mapCounters: ICounters;
  suppliers: ISupplier[];
  prevHotels: IHotel[];
  isMapMessageShown: boolean;
  isSessionExpired: boolean;
  bounds: IBounds;
  recentSearches: IHotelsRecentSearches[];
  errorRecentSearches: string;
  loadingRecentSearches: boolean;
}

const initialState: IHotelsState = {
  hotels: null,
  mapHotels: null,
  isMapView: false,
  loading: false,
  loadingMore: false,
  mapLoading: false,
  loadingFilters: false,
  error: '',
  isSearch: false,
  pageNumber: DEFAULT_PAGE_NUMBER,
  searchedLocation: '',
  counters: null,
  mapCounters: null,
  lastSearchedLocation: null,
  bounds: null,
  suppliers: [],
  prevHotels: [],
  isMapMessageShown: false,
  isSessionExpired: false,
  recentSearches: [],
  errorRecentSearches: '',
  loadingRecentSearches: false,
};

const hotelsSlice = createSlice({
  name: 'hotels',
  initialState,
  reducers: {
    setIsSearch: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isSearch = payload;
    },
    setBounds: (state: IHotelsState, { payload }: PayloadAction<IBounds>) => {
      state.bounds = payload;
    },
    setIsMapView: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isMapView = payload;
    },
    setLoading: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loading = payload;
    },
    setMapLoading: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.mapLoading = payload;
    },
    setLastSearchedLocation: (state: IHotelsState, { payload }: PayloadAction<ILocation>) => {
      state.lastSearchedLocation = payload;
    },
    setError: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.error = payload;
    },
    setHotels: (
      state: IHotelsState,
      { payload }: PayloadAction<{ hotels: IHotel[]; isLoadMore: boolean }>,
    ) => {
      state.prevHotels = payload.hotels;

      if (payload.isLoadMore) {
        state.hotels = [...(state.hotels || []), ...payload.hotels];
      } else {
        state.hotels = payload.hotels;
      }
    },
    resetHotels: (state: IHotelsState) => {
      state.prevHotels = [];
      state.hotels = [];
    },
    setSessionKey: (state: IHotelsState, { payload }: PayloadAction<ISessionKey>) => {
      state.sessionKey = payload;
    },
    setLoadingMore: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingMore = payload;
    },
    setLoadingFilters: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingFilters = payload;
    },
    setPageNumber: (state: IHotelsState, { payload }: PayloadAction<number | undefined>) => {
      state.pageNumber = payload ? payload : state.pageNumber + oneItem;
    },
    setSearchedLocation: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.searchedLocation = payload;
    },
    setCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters>) => {
      state.counters = payload;
    },
    setMapCounters: (state: IHotelsState, { payload }: PayloadAction<ICounters>) => {
      state.mapCounters = payload;
    },
    setSuppliers: (state: IHotelsState, { payload }: PayloadAction<ISupplier[]>) => {
      state.suppliers = payload;
    },
    setMapHotels: (state: IHotelsState, { payload }: PayloadAction<IHotel[]>) => {
      state.mapHotels = payload;
    },
    setIsMapMessageShown: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isMapMessageShown = payload;
    },
    setIsSessionExpired: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.isSessionExpired = payload;
    },
    setRecentSearches: (
      state: IHotelsState,
      { payload }: PayloadAction<IHotelsState['recentSearches']>,
    ) => {
      state.recentSearches = payload;
    },
    setLoadingRecentSearches: (state: IHotelsState, { payload }: PayloadAction<boolean>) => {
      state.loadingRecentSearches = payload;
    },
    setErrorRecentSearches: (state: IHotelsState, { payload }: PayloadAction<string>) => {
      state.errorRecentSearches = payload;
    },
  },
});

export const hotelsActions = hotelsSlice.actions;

export const hotelsReducer = hotelsSlice.reducer;

let cancelRequest: Canceler;
let expirationTimer: number;

export const setSessionExpirationTimer = (
  session: ISessionKey,
  dispatch: ThunkDispatch<RootState, undefined, Action>,
): void => {
  if (expirationTimer) {
    clearTimeout(expirationTimer);
  }

  // @ts-ignore
  expirationTimer = setTimeout(() => {
    dispatch(hotelsActions.setIsSessionExpired(true));
  }, getTimeout(session?.expireDate));
};

export const resetHotels = (): AppThunk => {
  return async (dispatch, getState) => {
    const { hotelsStore } = getState();

    if (!hotelsStore.isMapView) {
      dispatch(hotelsActions.setLoading(true));
    }
    dispatch(hotelsActions.resetHotels());
  };
};

export const getHotels = (
  location: ILocation,
  startDate: string,
  endDate: string,
  rooms: IRoom[],
  isLoadMore = false,
  isFiltersChange = false,
  isReuseSessionKey = false,
): AppThunk => {
  return async (dispatch, getState) => {
    const { hotelsStore } = getState();

    // initial setting hotels to show skeleton
    if (!hotelsStore.hotels) {
      dispatch(hotelsActions.setHotels({ hotels: [], isLoadMore: false }));
    }

    dispatch(hotelsActions.setSearchedLocation(location.name));
    dispatch(hotelsActions.setLastSearchedLocation(location));

    // cancel request if it's not pagination request
    if (cancelRequest) {
      cancelRequest();
    }

    dispatch(hotelsActions.setMapLoading(true));

    if (isLoadMore) {
      dispatch(hotelsActions.setLoadingMore(true));
    } else {
      dispatch(hotelsActions.setLoading(true));
      dispatch(hotelsActions.setIsSearch(true));
      dispatch(hotelsActions.setPageNumber(DEFAULT_PAGE_NUMBER));
    }

    if (isFiltersChange) {
      dispatch(hotelsActions.setPageNumber(DEFAULT_PAGE_NUMBER));
      dispatch(hotelsActions.setLoadingFilters(true));
    }

    try {
      const res: { data: IHotelsResponse } = await axiosInstance.post(
        Urls.Hotels,
        getSearchBody(
          location,
          startDate,
          endDate,
          rooms,
          getState().hotelsStore,
          getState().filtersStore,
          isLoadMore,
          isReuseSessionKey,
        ),
        {
          ...getHeaders(),
          cancelToken: new axios.CancelToken((canceler: Canceler) => {
            cancelRequest = canceler;
          }),
        },
      );

      if (res.data.bounds) {
        dispatch(hotelsActions.setBounds(res.data.bounds));
      } else if (!res.data.searchHotels?.length) {
        dispatch(
          hotelsActions.setBounds({
            ...hotelsStore?.bounds,
            northEast: {
              ...hotelsStore?.bounds?.northEast,
              latitude: hotelsStore?.bounds?.northEast.latitude + reRenderNum,
            },
          }),
        );
      }

      if (res.data?.sessionKey?.isInvalid) {
        dispatch(hotelsActions.setSessionKey(null));
        UrlUtils.removeFromUrl(SESSION_KEY_LABEL);
        dispatch(getHotels(location, startDate, endDate, rooms));
        return;
      }

      dispatch(hotelsActions.setLoading(false));
      dispatch(hotelsActions.setLoadingMore(false));
      dispatch(hotelsActions.setLoadingFilters(false));
      dispatch(hotelsActions.setSessionKey(res.data.sessionKey));

      if (res.data.sessionKey) {
        dispatch(hotelsActions.setIsSessionExpired(false));
        // @ts-ignore
        setSessionExpirationTimer(res.data.sessionKey, dispatch);
      }

      if (isFiltersChange) {
        dispatch(hotelsActions.setHotels({ hotels: res.data.searchHotels, isLoadMore: false }));
      } else {
        dispatch(hotelsActions.setHotels({ hotels: res.data.searchHotels, isLoadMore }));
      }

      if (res.data.counters) {
        dispatch(hotelsActions.setCounters(res.data.counters));
      }
    } catch (error) {
      console.error(error);

      dispatch(hotelsActions.setMapLoading(false));

      // error when user session ended
      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        error?.response?.data[zeroItem] === SESSION_EXPIRED_MESSAGE
      ) {
        dispatch(hotelsActions.setIsSessionExpired(true));
      }

      // error triggered by axios when we cancel request
      if (!(error instanceof axios.Cancel)) {
        dispatch(hotelsActions.setError(error.toString()));
        dispatch(hotelsActions.setLoadingMore(false));
        dispatch(hotelsActions.setLoadingFilters(false));
        dispatch(hotelsActions.setLoading(false));
      }
    }
  };
};

let cancelMapRequest: Canceler;

export const getMapHotels = (
  location: ILocation,
  startDate: string,
  endDate: string,
  rooms: IRoom[],
  hotelId: number = null,
): AppThunk => {
  return async (dispatch, getState) => {
    if (cancelMapRequest) {
      cancelMapRequest();
    }

    dispatch(hotelsActions.setMapHotels([]));
    dispatch(hotelsActions.setMapLoading(true));

    try {
      const options = getSearchBody(
        location,
        startDate,
        endDate,
        rooms,
        getState().hotelsStore,
        getState().filtersStore,
        false,
        false,
        hotelId,
      );
      const { hotelsStore } = getState();
      const { sessionKey } = hotelsStore;
      const data = {
        ...options,
        limit: DEFAULT_MAP_LIMIT,
        bounds: hotelsStore.bounds,
      };

      delete data.pageNumber;
      delete data.pageSize;

      data.sessionKey = sessionKey;

      const res: { data: IHotelsResponse } = await axiosInstance.post(Urls.HotelsMap, data, {
        ...getHeaders(),
        cancelToken: new axios.CancelToken((canceler: Canceler) => {
          cancelMapRequest = canceler;
        }),
      });

      if (res.data.bounds && hotelId) {
        dispatch(hotelsActions.setBounds(res.data.bounds));
      }

      dispatch(hotelsActions.setMapLoading(false));

      if (res.data.counters) {
        dispatch(hotelsActions.setMapCounters(res.data.counters));
      }

      dispatch(hotelsActions.setMapHotels(res.data.searchHotels));

      if (res.data.searchHotels.length === zeroItem) {
        dispatch(hotelsActions.setIsMapMessageShown(true));
      } else {
        dispatch(hotelsActions.setIsMapMessageShown(false));
      }
    } catch (error) {
      console.error(error);
      dispatch(hotelsActions.setMapLoading(false));

      if (
        error?.response?.status === SESSION_EXPIRED_STATUS &&
        error?.response?.data &&
        error?.response?.data[zeroItem] === SESSION_EXPIRED_MESSAGE
      ) {
        dispatch(hotelsActions.setIsSessionExpired(true));
      }
    }
  };
};

export const getSuppliers = (): AppThunk => {
  return async (dispatch) => {
    try {
      const res: { data: { suppliers: ISupplier[] } } = await axiosInstance.get(Urls.Suppliers, {
        ...getHeaders(),
      });
      dispatch(hotelsActions.setSuppliers(res.data.suppliers));
    } catch (error) {
      console.error(error);
    }
  };
};

export const getHotelsRecentSearches = (): AppThunk => {
  return async (dispatch) => {
    try {
      dispatch(hotelsActions.setLoadingRecentSearches(true));
      const { data } = await axiosInstance.get(Urls.HotelsRecentSearches, {
        ...getHeaders(),
      });
      dispatch(hotelsActions.setRecentSearches(data.recentSearches || []));
      dispatch(hotelsActions.setLoadingRecentSearches(false));
    } catch (error) {
      console.error(error);
      dispatch(hotelsActions.setErrorRecentSearches(error.toString()));
      dispatch(hotelsActions.setLoadingRecentSearches(false));
    }
  };
};
